"use client"

import { useEffect, useState } from "react"
import type { ChatUIMessage } from "@/schema"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import type { ToolUIPart } from "ai"
import { ChevronDown, ChevronRight, Wrench } from "lucide-react"

const TOOL_SUMMARY_KEYS = [
  "finished_message",
  "starting_message",
  "message",
  "summary",
  "result",
  "text",
  "description",
  "error",
] as const

type ToolLikePart =
  | Extract<ChatUIMessage["parts"][number], { type: "dynamic-tool" }>
  | Extract<ChatUIMessage["parts"][number], { type: `tool-${string}` }>

export interface ChatToolActivity {
  id: string
  name: string
  completed: boolean
  startingMessage?: string
  finishedMessage?: string
  errorText?: string
  statusMessages?: string[]
}

export interface ChatToolActivityCollection {
  activities: ChatToolActivity[]
  activityPartIndexes: Set<number>
  associatedStatusIndexes: Set<number>
}

function humanizeToolName(name: string) {
  return name
    .replace(/^tool-/, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function compactValueSummary(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return undefined

    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return compactValueSummary(JSON.parse(trimmed) as unknown)
      } catch {
        return undefined
      }
    }

    return trimmed.length > 280 ? `${trimmed.slice(0, 277).trim()}...` : trimmed
  }

  if (isRecord(value)) {
    for (const key of TOOL_SUMMARY_KEYS) {
      const summary = compactValueSummary(value[key])
      if (summary) return summary
    }
  }

  return undefined
}

function compactInputMessage(value: unknown): string | undefined {
  if (!isRecord(value)) return compactValueSummary(value)

  return compactValueSummary(value.message) ?? compactValueSummary(value.query) ?? compactValueSummary(value.prompt)
}

function isCompletedToolState(state: ToolUIPart["state"]) {
  return state === "output-available" || state === "output-denied" || state === "output-error"
}

function isToolLikePart(part: ChatUIMessage["parts"][number]): part is ToolLikePart {
  return part.type === "dynamic-tool" || part.type.startsWith("tool-")
}

function isToolActivityPart(part: ChatUIMessage["parts"][number]) {
  return part.type === "data-tool-call" || part.type === "data-automation-action" || isToolLikePart(part)
}

function toolActivityFromPart(
  part: ChatUIMessage["parts"][number],
  index: number,
  statusMessages?: string[]
): ChatToolActivity | null {
  if (part.type === "data-tool-call" || part.type === "data-automation-action") {
    return {
      id: part.id ?? part.data.id ?? `tool-activity-${index}`,
      name: part.data.name,
      completed: !!part.data.finished_message,
      startingMessage: part.data.starting_message,
      finishedMessage: part.data.finished_message,
      statusMessages,
    }
  }

  if (!isToolLikePart(part)) return null

  const toolName = part.type === "dynamic-tool" ? part.toolName : part.type.replace(/^tool-/, "")
  return {
    id: `tool-activity-${index}`,
    name: part.title ?? toolName,
    completed: isCompletedToolState(part.state),
    startingMessage: "input" in part ? compactInputMessage(part.input) : undefined,
    finishedMessage: "output" in part ? compactValueSummary(part.output) : undefined,
    errorText: "errorText" in part ? part.errorText : undefined,
    statusMessages,
  }
}

export function collectChatToolActivities(message: ChatUIMessage): ChatToolActivityCollection {
  const statusByPartIndex = new Map<number, string[]>()
  const associatedStatusIndexes = new Set<number>()
  const activityPartIndexes = new Set<number>()
  let latestToolActivityPartIndex: number | null = null

  message.parts.forEach((part, index) => {
    if (isToolActivityPart(part)) {
      latestToolActivityPartIndex = index
      activityPartIndexes.add(index)
      return
    }

    if (part.type === "data-status") {
      if (latestToolActivityPartIndex !== null && part.data.message) {
        const existing = statusByPartIndex.get(latestToolActivityPartIndex) ?? []
        statusByPartIndex.set(latestToolActivityPartIndex, [...existing, part.data.message])
        associatedStatusIndexes.add(index)
      }
      return
    }

    latestToolActivityPartIndex = null
  })

  const activities = message.parts.flatMap((part, index) => {
    const activity = toolActivityFromPart(part, index, statusByPartIndex.get(index))
    return activity ? [activity] : []
  })

  return { activities, activityPartIndexes, associatedStatusIndexes }
}

function toolActivityLabel(activity: ChatToolActivity) {
  if (activity.errorText) return activity.errorText

  const latestStatus = activity.statusMessages?.at(-1)
  if (!activity.completed) {
    return latestStatus ?? activity.startingMessage ?? `Running ${humanizeToolName(activity.name)}`
  }

  return (
    activity.finishedMessage ?? latestStatus ?? activity.startingMessage ?? `Used ${humanizeToolName(activity.name)}`
  )
}

function ToolActivityLine({ activity, running = false }: { activity: ChatToolActivity; running?: boolean }) {
  return (
    <div className="text-muted-foreground flex w-fit min-w-0 items-start gap-2 text-sm">
      <Wrench className="mt-0.5 size-4 shrink-0" />
      <span className={cn("min-w-0", running && "animate-text-shimmer")}>{toolActivityLabel(activity)}</span>
    </div>
  )
}

function LiveToolActivityGroup({ activities }: { activities: ChatToolActivity[] }) {
  const [isOpen, setIsOpen] = useState(false)
  const runningActivity = activities.findLast((activity) => !activity.completed)
  const latestActivity = runningActivity ?? activities.at(-1)
  const hasMultipleCompletedActivities = !runningActivity && activities.length > 1

  useEffect(() => {
    if (runningActivity) setIsOpen(false)
  }, [runningActivity])

  if (!latestActivity) return null

  if (!hasMultipleCompletedActivities) {
    return <ToolActivityLine activity={latestActivity} running={!!runningActivity} />
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/tool-activity not-prose w-fit max-w-full">
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex max-w-full items-start gap-2 rounded-sm text-left text-sm transition-colors outline-none focus-visible:ring-2">
        <Wrench className="mt-0.5 size-4 shrink-0" />
        <span className="min-w-0">{toolActivityLabel(latestActivity)}</span>
        <ChevronDown className="mt-0.5 size-4 shrink-0 transition-transform group-data-[state=open]/tool-activity:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <div className="border-muted mt-2 ml-2 space-y-2 border-l pl-4">
          {activities.map((activity) => (
            <ToolActivityLine key={activity.id} activity={activity} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function formatWorkDuration(message: ChatUIMessage) {
  const startedAtValue = message.metadata?.created_at
  const completedAtValue = message.metadata?.completed_at
  if (!startedAtValue || !completedAtValue) return null

  const startedAt = new Date(startedAtValue).getTime()
  const completedAt = new Date(completedAtValue).getTime()
  if (Number.isNaN(startedAt) || Number.isNaN(completedAt) || completedAt < startedAt) return null

  const totalSeconds = Math.max(1, Math.round((completedAt - startedAt) / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes === 0) return `${totalSeconds}s`
  if (seconds === 0) return `${minutes}m`
  return `${minutes}m ${seconds}s`
}

function CompletedToolActivityGroup({
  activities,
  message,
}: {
  activities: ChatToolActivity[]
  message: ChatUIMessage
}) {
  const [isOpen, setIsOpen] = useState(false)
  const duration = formatWorkDuration(message)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/work-summary not-prose w-full">
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex items-center gap-1 rounded-sm text-sm transition-colors outline-none focus-visible:ring-2">
        <span>{duration ? `Worked for ${duration}` : "Worked"}</span>
        <ChevronRight className="size-4 transition-transform group-data-[state=open]/work-summary:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <div className="my-2 space-y-2">
          {activities.map((activity) => (
            <ToolActivityLine key={activity.id} activity={activity} />
          ))}
        </div>
      </CollapsibleContent>
      <div className="border-border mt-1 border-b" />
    </Collapsible>
  )
}

export function ChatToolActivityDisplay({
  activities,
  message,
  isAnimating,
}: {
  activities: ChatToolActivity[]
  message: ChatUIMessage
  isAnimating: boolean
}) {
  if (activities.length === 0) return null

  const hasFinalAnswer = message.parts.some((part) => part.type === "text" && part.text.trim().length > 0)
  const shouldCollapseCompletedActivity =
    message.role === "assistant" && !isAnimating && hasFinalAnswer && activities.every((activity) => activity.completed)

  return shouldCollapseCompletedActivity ? (
    <CompletedToolActivityGroup activities={activities} message={message} />
  ) : (
    <LiveToolActivityGroup activities={activities} />
  )
}
